import { Link } from 'react-router';
import { ArticleSchema, BreadcrumbSchema, SEO } from '@/components/seo/seo';

const bots = [
  ['Googlebot', 'Discovers indexable HTML, data and sitemap URLs under the general robots policy.'],
  ['OAI-SearchBot', 'May discover public search content under the general robots policy.'],
  ['ChatGPT-User', 'Fetches a public page when a user asks ChatGPT to access it.'],
  ['PerplexityBot', 'May discover public HTML and data under the general robots policy.'],
] as const;

export function CrawlerAndCitationAccessPage() {
  return (
    <div className="min-h-screen bg-surface-50">
      <SEO
        title="Crawler and Citation Access | Fixtyer"
        description="How Fixtyer exposes public HTML, data sources and citation guidance to search and AI user agents."
        canonical="/crawler-and-citation-access"
        type="article"
        article={{
          publishedTime: '2026-07-19',
          modifiedTime: '2026-07-19',
          author: 'Fixtyer',
        }}
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Crawler and citation access', url: '/crawler-and-citation-access' },
        ]}
      />
      <ArticleSchema
        title="Crawler and citation access"
        description="Fixtyer public-content access and citation documentation."
        url="/crawler-and-citation-access"
        datePublished="2026-07-19"
        dateModified="2026-07-19"
        author="Fixtyer"
      />
      <header className="bg-brand-950 text-white">
        <div className="mx-auto max-w-4xl px-4 py-16 sm:px-6">
          <h1 className="font-display text-4xl font-bold">Crawler and citation access</h1>
          <p className="mt-5 text-lg text-brand-100">
            Fixtyer publishes the Index as crawlable, prerendered public HTML and
            downloadable JSON and CSV. Those public pages are the primary source.
          </p>
        </div>
      </header>
      <main className="mx-auto max-w-4xl space-y-10 px-4 py-12 sm:px-6">
        <section>
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            User-agent access
          </h2>
          <div className="overflow-x-auto rounded-xl border border-surface-200 bg-white">
            <table className="w-full">
              <caption className="p-4 text-left font-semibold">
                Public discovery behavior documented on 19 July 2026
              </caption>
              <thead className="bg-surface-100">
                <tr>
                  <th className="p-3 text-left" scope="col">User agent</th>
                  <th className="p-3 text-left" scope="col">Public access</th>
                </tr>
              </thead>
              <tbody>
                {bots.map(([name, note]) => (
                  <tr key={name} className="border-t border-surface-200">
                    <th className="p-3 text-left" scope="row">{name}</th>
                    <td className="p-3">{note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-4">
            Private application paths remain disallowed. The Road Maintenance Index,
            regional pages, press release, public HTML and data downloads are not
            disallowed by the current robots policy.
          </p>
        </section>

        <section>
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Citation sources
          </h2>
          <ul className="list-disc space-y-2 pl-6">
            <li>
              <Link className="text-brand-700 underline" to="/road-maintenance-index">
                Fixtyer Road Maintenance Index Q3 2026
              </Link>
            </li>
            <li>
              <Link
                className="text-brand-700 underline"
                to="/data/council-road-maintenance-ratings"
              >
                Canonical methodology and versioned downloads
              </Link>
            </li>
            <li>
              <a
                className="text-brand-700 underline"
                href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              >
                Department for Transport primary source
              </a>
            </li>
          </ul>
        </section>

        <section>
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            About llms.txt
          </h2>
          <p>
            llms.txt is an optional, secondary discovery aid. It does not replace
            public HTML, structured data, the sitemap, robots.txt or source links,
            and its publication does not guarantee that any crawler will use or honour it.
          </p>
        </section>

        <section className="rounded-xl border border-surface-200 bg-white p-5 text-sm">
          <p>Published: 19 July 2026</p>
          <p>Last reviewed: 19 July 2026</p>
          <p>Content owner: Elevyn Technology Group Limited editorial team</p>
          <p>
            <a
              className="text-brand-700 underline"
              href="https://fixtyer.co.uk/data/council-road-maintenance-ratings#corrections"
            >
              Report a factual correction
            </a>
          </p>
        </section>
      </main>
    </div>
  );
}
